import api, { isAuthReady } from "../services/backendService";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import keys from "configs/constants";
import utils from "utils";

import { store } from "../index";
import {
  updateControllersFromSignalR,
  updateSignalRState,
  updateAlertFromSignalR,
  updateLastGatewayStatusFromSignalR,
  updateGatewayFromSignalR,
  setParameterFileList,
} from "../redux/actions/index";
import {
  setControllersByGatewayGuidAsync,
  reconcileAlertsAsync,
} from "../redux/actions/thunk";
import { signalREventEmitter } from "../hooks/useSignalREvent";

// Local kill switch for the real-time stream (VITE_DISABLE_SIGNALR=true).
// Short-circuits both entry points, so nothing connects, retries, or dispatches
// stream state. The UI still seeds from REST on mount but stops auto-updating.
const SIGNALR_DISABLED = import.meta.env.VITE_DISABLE_SIGNALR === "true";

const RETRY_CONNECTION_INTERVAL = 5000;
// Reconnect backoff (ms): immediate, then increasing up to 30s, then steady.
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];

// Background tabs throttle timers, so SignalR drops/reconnects on every
// backgrounding with no real network issue. A backgrounding reconnect
// settles in 1-2s; a real outage stays down longer. Only notify if still
// down after this much VISIBLE time.
const NETWORK_ISSUE_GRACE_MS = 7000;

// When a gateway comes back online the backend does NOT reliably stream an alert
// OFF for its connectivity alarms (notably "No Internet"), so alerts.byId keeps a
// stale active alert (over-reporting every count/highlight derived from it).
// Reconcile the alerts slice from REST on the offline->online edge, debounced so a
// burst of gateways reconnecting after a network blip triggers a single refetch.
const ALERTS_RECONCILE_DEBOUNCE_MS = 5000;
let lastAlertsReconcileAt = 0;
const reconcileAlertsOnGatewayOnline = () => {
  const now = Date.now();
  if (now - lastAlertsReconcileAt < ALERTS_RECONCILE_DEBOUNCE_MS) return;
  lastAlertsReconcileAt = now;
  store.dispatch(reconcileAlertsAsync());
};

let sigRConnection;
let retryConnection;
// The single timer behind the network-issue check — armed only while
// visible, re-armed from zero on each return to foreground.
let reconnectingGraceTimer;
// True while the connection is down (auto reconnect or a deliberate
// reconnectSignalR stop) until the next successful connect. No separate
// "intentional" flag needed — a deliberate reconnect just settles fast
// enough to never reach the timer.
let isReconnecting = false;
// Attached only for the duration of a "down" episode; removed once resolved.
let visibilityHandler = null;

const stopWatchingReconnect = () => {
  clearTimeout(reconnectingGraceTimer);
  if (visibilityHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityHandler);
  }
  visibilityHandler = null;
};

// Arms/clears the grace timer to match visibility: hidden -> paused,
// visible -> counts NETWORK_ISSUE_GRACE_MS from now.
const watchReconnectVisibility = () => {
  if (!isReconnecting) return stopWatchingReconnect();

  clearTimeout(reconnectingGraceTimer);
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return;
  }
  reconnectingGraceTimer = setTimeout(() => {
    if (isReconnecting) store.dispatch(updateSignalRState({ connection: false }));
  }, NETWORK_ISSUE_GRACE_MS);
};

// Marks the connection down and starts the grace timer. Used identically by
// onreconnecting and onclose — one rule regardless of why we're down.
const beginReconnectEpisode = () => {
  isReconnecting = true;
  stopWatchingReconnect();
  visibilityHandler = watchReconnectVisibility;
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", visibilityHandler);
  }
  watchReconnectVisibility();
};

// Called by every path that re-establishes the connection.
const endReconnectEpisode = () => {
  isReconnecting = false;
  stopWatchingReconnect();
};

// True once we have successfully connected at least once. Used to distinguish
// the very first connect (initial seed handled by the page mount) from a
// reconnect (which must re-seed the store to stay consistent without a refresh).
let hasConnectedOnce = false;

// Always resolve a fresh access token. Passed as accessTokenFactory so the
// token is re-read on every (re)connect instead of being captured once in a
// closure — otherwise a reconnect after token expiry fails silently and only a
// page refresh (which re-authenticates) restores the stream.
const getAccessToken = async () => {
  const auth = await api.getAPICallAuthHeader();
  // isAuthReady() only rules out the sentinel states (AUTH_LOGOUT,
  // AUTH_PENDING, null/undefined) -- a "ready" object can still be missing
  // headers (e.g. authenticated but no token resolved yet), so the property
  // chain itself still needs the optional chaining.
  return isAuthReady(auth) ? auth?.headers?.Authorization?.split(" ")[1] : undefined;
};

const connectToSignalR = async () => {
  if (SIGNALR_DISABLED) return;
  // Avoid building a second connection if one already exists / is starting.
  if (sigRConnection) return;

  const token = await getAccessToken();
  if (!token) {
    // Not authenticated yet (e.g. AUTH_PENDING while MSAL's redirect is in
    // flight) — retry shortly. Track it as a reconnect episode, but only
    // once: this branch re-fires every RETRY_CONNECTION_INTERVAL, and
    // beginReconnectEpisode() would otherwise restart the grace timer on
    // every retry and it would never elapse. Once armed here, the same
    // timer that a real connection drop uses fires on schedule regardless
    // of how many retries happen in the meantime.
    if (!isReconnecting) beginReconnectEpisode();
    retryConnection = setTimeout(connectToSignalR, RETRY_CONNECTION_INTERVAL);
    return;
  }

  sigRConnection = new HubConnectionBuilder()
    .withUrl(keys.SIGNALR_HUB_URL, {
      accessTokenFactory: async () => (await getAccessToken()) ?? "",
    })
    .withAutomaticReconnect(RECONNECT_DELAYS)
    .configureLogging(LogLevel.Warning)
    .build();

  sigRConnection.serverTimeoutInMilliseconds = 60000;
  sigRConnection.keepAliveIntervalInMilliseconds = 15000;
  sigRConnection.on("broadcastTelemetry", (data) => {
    const storeState = store.getState();

    if (!storeState.signalR.connection) {
      store.dispatch(updateSignalRState({ connection: true }));
    }

    if (storeState.controllers.allIds.length > 0) {
      store.dispatch(updateControllersFromSignalR(data));
    }
  });
  sigRConnection.on("broadcastAlertStatus", (data) => {
    if (!store.getState().signalR.connection) {
      store.dispatch(updateSignalRState({ connection: true }));
    }

    store.dispatch(updateAlertFromSignalR(data));
    
    signalREventEmitter.emit("broadcastAlertStatus", data);
  });
  sigRConnection.on("broadcastCurrentGatewayStatus", async (data) => {
    if (!store.getState().signalR.connection) {
      store.dispatch(updateSignalRState({ connection: true }));
    }
    store.dispatch(updateLastGatewayStatusFromSignalR(data));
    signalREventEmitter.emit("broadcastCurrentGatewayStatus", data);

    const { status, oldStatus } = data;

    if (status.modbus === 4 && oldStatus.modbus < 4) {
      // End of Modbus scan, refresh controllers list.
      // Only use setControllersByGatewayGuidAsync for site users who have
      // site tree data in Redux. Fleet users get controller updates via the
      // component-level useSignalREvent listener in EditGatewayProperties.
      const storeState = store.getState();
      const gw = storeState.gateways.byId[data.gatewayGuid];
      if (gw && storeState.sites.byId[gw.site?.siteGuid]) {
        store.dispatch(setControllersByGatewayGuidAsync(data.gatewayGuid));
      }
    }
  });
  sigRConnection.on("broadcastGatewayStatus", async (data) => {
    const storeState = store.getState();
    const isAdmin = storeState.user.role === "Admin";

    if (!storeState.signalR.connection) {
      store.dispatch(updateSignalRState({ connection: true }));
    }

    signalREventEmitter.emit("broadcastGatewayStatus", data);

    // Gateway just came back online: its connectivity alarms have no guaranteed
    // alert-OFF stream, so refetch the alert snapshot to drop any stale ones.
    if (data.status === 1 && data.oldStatus === 0) {
      reconcileAlertsOnGatewayOnline();
    }

    if (
      storeState.gateways.allIds.filter((guid) => guid === data.gatewayGuid)
        .length === 0 &&
      !isAdmin
    ) {
      return;
    }

    let releaseFW = await utils.getGatewayRelease(data.gatewayGuid, isAdmin);

    if (data.status === 1 && data.oldStatus === 0) {
      releaseFW = await utils.getGatewayReleaseAsync(
        data.gatewayGuid,
        isAdmin,
        storeState
      );
    }

    let newData = { ...data };

    newData.releaseFW = releaseFW;

    if (data.lastSeenOn) store.dispatch(updateGatewayFromSignalR(newData));
    else {
      store.dispatch(setParameterFileList(data));
    }
  });
  sigRConnection.on("broadcastUINotifications", (data) => {
    const message = JSON.parse(data ?? "{}");
    const { gatewayGuid, properties } = message;
    const deviceProperties = properties.reported.device;
    const storeState = store.getState();
    const isAdmin = storeState.user.role === "Admin";

    if (!storeState.signalR.connection) {
      store.dispatch(updateSignalRState({ connection: true }));
    }

    if (
      storeState.gateways.allIds.filter((guid) => guid === gatewayGuid)
        .length === 0 &&
      !isAdmin
    ) {
      return;
    }

    const isValidDad = deviceProperties.dad !== undefined && deviceProperties.dad !== null;

    const gatewayUpdate = {
        gatewayGuid,
        realTimeMode: deviceProperties.rtmode,
        disableAutomaticDetection: isValidDad ? deviceProperties.dad === 1 : undefined,
        interframeDelay: deviceProperties.iframems,
      };
    store.dispatch(
      updateGatewayFromSignalR(gatewayUpdate)
    );
  });

  sigRConnection.onreconnecting((error) => {
    utils.debug("SignalR reconnecting: " + (error?.message ?? ""));
    beginReconnectEpisode();
  });

  sigRConnection.onreconnected((connectionId) => {
    utils.debug("SignalR reconnected: " + connectionId);
    endReconnectEpisode();
    store.dispatch(updateSignalRState({ connection: true }));
    // On reconnect the backend re-runs OnConnectedAsync and recomputes the
    // gateway groups from the DB. Any messages produced during the gap are
    // lost (at-most-once), so re-seed the store from REST to restore
    // consistency automatically — what a manual page refresh used to do.
    signalREventEmitter.emit("signalrReconnected");
  });

  sigRConnection.onclose((error) => {
    // Reached on real automatic-reconnect exhaustion OR a deliberate
    // reconnectSignalR() stop — same grace timer as onreconnecting decides
    // either way.
    utils.debug("SignalR connection closed: " + (error?.message ?? ""));
    beginReconnectEpisode();
    sigRConnection = null;
    retryConnection = setTimeout(connectToSignalR, RETRY_CONNECTION_INTERVAL);
  });

  try {
    await sigRConnection.start();
    utils.debug("Connected to SignalR Service");
    endReconnectEpisode();
    store.dispatch(updateSignalRState({ connection: true }));
    if (hasConnectedOnce) {
      // A successful start that is not the first one is a recovery after the
      // automatic reconnect gave up and we rebuilt the connection — re-seed.
      signalREventEmitter.emit("signalrReconnected");
    }
    hasConnectedOnce = true;
  } catch (error) {
    utils.debug("SignalR error: " + error.message);
    store.dispatch(updateSignalRState({ connection: false }));
    sigRConnection = null;
    retryConnection = setTimeout(connectToSignalR, RETRY_CONNECTION_INTERVAL);
  }
};

const reconnectSignalR = async () => {
  if (SIGNALR_DISABLED) return;
  if (sigRConnection) {
    try {
      await sigRConnection.stop();
    } catch (error) {
      utils.debug("SignalR stop error: " + error.message);
    }
  }
  clearTimeout(retryConnection);
  await connectToSignalR();
};

const azureSignalR = { connectToSignalR, reconnectSignalR };

export default azureSignalR;
